import React from "react";

import { DataGrid } from "@mui/x-data-grid";

import Box from "@mui/material/Box";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { useTranslation } from "react-i18next";

function isOverflown(element) {
    return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
}
const GridCellExpand = React.memo(function GridCellExpand(props) {
    const { width, value } = props;
    const wrapper = React.useRef(null);
    const cellDiv = React.useRef(null);
    const cellValue = React.useRef(null);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [showFullCell, setShowFullCell] = React.useState(false);
    const [showPopper, setShowPopper] = React.useState(false);
    const handleMouseEnter = () => {
        const isCurrentlyOverflown = isOverflown(cellValue.current);
        setShowPopper(isCurrentlyOverflown);
        setAnchorEl(cellDiv.current);
        setShowFullCell(true);
    };
    const handleMouseLeave = () => {
        setShowFullCell(false);
    };
    React.useEffect(() => {
        if (!showFullCell) {
            return undefined;
        }
        function handleKeyDown(nativeEvent) {
            // IE11, Edge (prior to using Bink?) use 'Esc'
            if (nativeEvent.key === "Escape" || nativeEvent.key === "Esc") {
                setShowFullCell(false);
            }
        }
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [setShowFullCell, showFullCell]);
    return (
        <Box
            ref={wrapper}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
                alignItems: "center",
                lineHeight: "24px",
                width: "100%",
                height: "100%",
                position: "relative",
                display: "flex",
            }}
        >
            <Box
                ref={cellDiv}
                sx={{
                    height: "100%",
                    width,
                    display: "block",
                    position: "absolute",
                    top: 0,
                }}
            />
            <Box
                ref={cellValue}
                sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {value}
            </Box>
            {showPopper && (
                <Popper open={showFullCell && anchorEl !== null} anchorEl={anchorEl} style={{ width, marginLeft: -17 }}>
                    <Paper elevation={1} style={{ minHeight: wrapper.current.offsetHeight - 3 }}>
                        <Typography variant="body2" style={{ padding: 8 }}>
                            {value}
                        </Typography>
                    </Paper>
                </Popper>
            )}
        </Box>
    );
});
function renderCellExpand(params) {
    return <GridCellExpand value={params.formattedValue || ""} width={displayTextWidth(params.formattedValue, ".9rem Proxima Nova Rg")} />;
}
function displayTextWidth(text, font) {
    let canvas = displayTextWidth.canvas || (displayTextWidth.canvas = document.createElement("canvas"));
    let context = canvas.getContext("2d");
    context.font = font;
    let metrics = context.measureText(text);
    return metrics.width + 30;
}
export default function Basic(props) {
    const { t } = useTranslation();
    let rows = [];

    for (const key of Object.keys(props.messages)) {
        const sentDate = props.messages[key]["dateReceivedUTC.ISO8601"];
        const ackDate = props.messages[key]["dateAcknowledgedUTC.ISO8601"];
        
        rows.push({
            id: key,
            dateReceived: sentDate ? new Date(sentDate) : null,
            dateAcknowledged: ackDate && ackDate !== "" ? new Date(ackDate) : null,
            status: props.messages[key]["newMessage"],
            subject: props.messages[key]["Subject"],
            newMessage: props.messages[key]["newMessage"],
        });
    }

    const columns = [
        {
            headerName: "Receieved",
            field: "dateReceived",
            minWidth: 300,
            sortingOrder: ['asc', 'desc'],
            sortComparator: (v1, v2) => {
                if (!v1 && !v2) return 0;
                if (!v1) return -1;
                if (!v2) return 1;
                return v1.getTime() - v2.getTime();
            },
            valueFormatter: (value) => {
                if (!value) return "";
                return Intl.DateTimeFormat("en-CA", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "numeric",
                    minute: "numeric",
                }).format(value);
            },
        },
        
        {
            headerName: "Subject",
            field: "subject",
            flex: 1,
            minWidth: 200,
            sortingOrder: ['asc', 'desc'],
            renderCell: (params) => {
                return params.row.newMessage ? (
                    <div className="unreadMessage">
                        <p>{params.value}</p>
                    </div>
                ) : (
                    <div className="readMessage">
                        <p>{params.value}</p>
                    </div>
                );
            },
        },
    ];

    return (
      <div style={{ height: 200, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          onRowClick={(params) => props.toggleMessage(params.id)}
          initialState={{
            sorting: {
              sortModel: [{ field: "dateReceived", sort: "desc" }],
            },
          }}
          hideFooter
          localeText={{
            noRowsLabel: t("recentservice.nomatches"),
          }}
          sx={{ "--DataGrid-overlayHeight": "300px" }}
        />
      </div>
    );
}
